import { type CSSProperties, useEffect, useRef, useState } from 'react';
import { radio } from '../brand';

/**
 * DJ Longbeard Radio — a "now playing" bar for the future 24/7 stream. It's
 * always streaming, so there's no play/pause: only a mute toggle (right).
 * - Active (unmuted): the bar brightens and the equalizer runs at full peak.
 * - Muted: it goes dimmer/grayer but the bars KEEP moving at a lower peak, so
 *   it still reads as "live right now".
 * The "Tap to unmute" tooltip shows on load, fades out after a few seconds, and
 * returns on hover of the mute button (matching the song link's hover tooltip).
 */
// per-bar profile → an organic, music-like spectrum: each bar peaks to a
// different height (p) at its own rate (d) and offset (l), so the equalizer
// dances like a real frequency analyzer rather than a uniform wave.
const BARS: { p: number; d: number; l: number }[] = [
  { p: 0.5, d: 0.62, l: 0.0 }, { p: 0.85, d: 0.95, l: 0.18 }, { p: 0.42, d: 0.7, l: 0.42 },
  { p: 1.0, d: 1.15, l: 0.1 }, { p: 0.66, d: 0.8, l: 0.34 }, { p: 0.9, d: 1.05, l: 0.06 },
  { p: 0.54, d: 0.66, l: 0.5 }, { p: 0.96, d: 1.2, l: 0.22 }, { p: 0.6, d: 0.78, l: 0.4 },
  { p: 0.8, d: 0.92, l: 0.14 }, { p: 0.45, d: 0.58, l: 0.46 }, { p: 0.74, d: 1.08, l: 0.28 },
  { p: 0.7, d: 0.85, l: 0.08 }, { p: 0.88, d: 1.0, l: 0.38 }, { p: 0.52, d: 0.72, l: 0.16 },
];

const SpeakerIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
    <path d="M4 9v6h3.5L12 19V5L7.5 9H4z" />
    <path d="M15.5 8.5a4 4 0 010 7" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);
const MutedIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
    <path d="M4 9v6h3.5L12 19V5L7.5 9H4z" />
    <path d="M16 9.5l4 5M20 9.5l-4 5" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
  </svg>
);

export function RadioBar() {
  const [muted, setMuted] = useState(true);
  const [tipOpen, setTipOpen] = useState(true);
  const ref = useRef<HTMLDivElement>(null);
  const fadeRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearFade = () => {
    if (fadeRef.current) clearTimeout(fadeRef.current);
    fadeRef.current = null;
  };
  const scheduleFade = (ms: number) => {
    clearFade();
    fadeRef.current = setTimeout(() => setTipOpen(false), ms);
  };

  useEffect(() => {
    scheduleFade(4200); // show on load, then fade away
    function onDocClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setTipOpen(false);
    }
    document.addEventListener('click', onDocClick);
    return () => {
      document.removeEventListener('click', onDocClick);
      clearFade();
    };
  }, []);

  return (
    <div className={`radio${muted ? '' : ' is-active'}`} ref={ref} aria-label={radio.name}>
      <div className="radio__bar">
        <div className={`viz ${muted ? 'viz--muted' : 'viz--live'}`} aria-hidden="true">
          {BARS.map((b, i) => (
            <span
              key={i}
              className="viz__bar"
              style={{ '--peak': b.p, animationDuration: `${b.d}s`, animationDelay: `${b.l}s` } as CSSProperties}
            />
          ))}
        </div>

        <a className="radio__now" href={radio.youtubeUrl} target="_blank" rel="noopener noreferrer">
          <span className="radio__nowtext">
            <span className="radio__track">&ldquo;{radio.nowPlaying.track}&rdquo;</span>{' '}
            <span className="radio__by">by {radio.nowPlaying.artist}</span>
          </span>
          <svg className="radio__ext" viewBox="0 0 24 24" width="13" height="13" aria-hidden="true">
            <path d="M7 17L17 7M9 7h8v8" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span className="sr-only">— listen on YouTube (opens in a new tab)</span>
          <span className="radio__nowtip" aria-hidden="true">Listen on YouTube</span>
        </a>

        <div className="radio__mutewrap">
          <button
            type="button"
            className={`radio__mute${muted ? ' is-muted' : ''}`}
            aria-label={muted ? 'Unmute radio' : 'Mute radio'}
            aria-pressed={!muted}
            onMouseEnter={() => {
              clearFade();
              setTipOpen(true);
            }}
            onMouseLeave={() => scheduleFade(1600)}
            onClick={() => {
              setMuted((m) => !m);
              setTipOpen(false);
              clearFade();
            }}
          >
            {muted ? <MutedIcon /> : <SpeakerIcon />}
            <span>{muted ? 'Listen' : 'Mute'}</span>
          </button>
          {muted && (
            <div className={`radio__tip${tipOpen ? ' is-open' : ''}`} role="tooltip">
              Tap to unmute
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
